<template>
  <hr-form-dialog
    ref="dialog"
    size="md"
    :title="showProgramModificationFields
      ? $t('editProgram.programModificationsDialog.textTitle')
      : $t('editProgram.programModificationsDialog.headingAddToSession')"
    data-e2e="dialog-program-modifications"
    @hide="onDialogHide"
  >
    <div class="row q-col-gutter-md">
      <template v-if="showProgramModificationFields">
        <div class="col-12">
          <hr-info-banner
            :message="$t('editProgram.programModificationsDialog.info')"
          />
        </div>
        <hr-form-section-header
          :label="$t('editProgram.programModificationsDialog.headingAddActivityNote')"
          class="col-12"
        />
        <hr-text-field
          v-model="activityNotes"
          multiline
          :label="$t('editProgram.programModificationsDialog.inputNotesLabel')"
          hide-bottom-space
          class="col-12"
        />
        <template v-if="!isNewProgram && hasTherapistCredentials">
          <hr-form-section-header
            :label="$t('editProgram.programModificationsDialog.headingAddPhaseLine')"
            class="col-12"
          />
          <div class="col-12">
            <hr-checkbox
              v-model="addPhaseLine"
              :label="$t('editProgram.programModificationsDialog.labelAddPhaseLine')"
            />
          </div>
          <hr-text-field
            v-if="addPhaseLine"
            v-model="addPhaseLineLabel"
            :label="$t('editProgram.programModificationsDialog.inputAddPhaseLine')"
            hide-bottom-space
            class="col-12"
          />
        </template>
      </template>
      <template v-if="canAddToSession">
        <p class="col-12 q-ma-none">
          {{ isBulkMode
            ? $t('editProgram.programModificationsDialog.subheadingAddToSessionMultiple')
            : $t('editProgram.programModificationsDialog.subheadingAddToSessionSingle')
          }}
        </p>
        <div class="col-12">
          <hr-checkbox
            v-for="session in visibleScheduledSessions"
            :key="session.id"
            :model-value="addToSession(session.id)"
            :label="session.sessionName"
            class="full-width"
            @update:model-value="onSetAddToSession(session.id, $event)"
          />
        </div>
        <template v-if="showWarningBlock">
          <div class="col-12">
            <hr-warning-banner
              :message="isBulkMode
                ? $t('editProgram.programModificationsDialog.waitingStatusWarningMultiple')
                : $t('editProgram.programModificationsDialog.waitingStatusWarningSingle')"
            />
          </div>
          <hr-list-select
            v-model="newProgramStatus"
            :options="statusOptions"
            :label="$t('editProgram.programModificationsDialog.labelSelectStatus')"
            hide-bottom-space
            class="col-12"
          />
        </template>
      </template>
    </div>
    <template #actions>
      <hr-dismiss-btn
        :label="$t(`common.${isManuallyOpened ? 'buttonCancel' : 'buttonSkip'}`)"
        data-e2e="button-skip-program-modifications"
        @click="hide"
      />
      <hr-confirm-btn
        :label="$t('common.buttonApply')"
        :loading="isSaving"
        :disable="!canSave"
        @click="onSubmit"
      />
    </template>
  </hr-form-dialog>
</template>
<script>
import { programStatuses } from 'boot/globals';
import { Timestamp } from 'firebase/firestore';
import { chain, filter, isEmpty, map, size, without } from 'lodash';
import { mapState } from 'pinia';
import {
  HrCheckbox,
  HrConfirmBtn,
  HrDismissBtn,
  HrFormDialog,
  HrFormSectionHeader,
  HrInfoBanner,
  HrListSelect,
  HrTextField,
  HrWarningBanner,
} from 'src/design-system';
import { useTime } from 'src/composables/time';
import ChartPhaseLinesDB from 'src/fb/chartPhaseLines-db';
import ProgramsDB from 'src/fb/programs-db';
import SessionProgramsDB from 'src/fb/sessionPrograms-db';
import Activities from 'src/mixins/Activities';
import ProgramsMixin from 'src/mixins/ProgramsMixin';
import Toolbox from 'src/mixins/Toolbox';
import { auth } from 'src/store/auth';
import { sessions } from 'src/store/sessions';

const INACTIVE_STATUSES = ['waiting', 'hold', 'discontinued', 'closed'];
const AVAILABLE_STATUS_OPTIONS = ['baseline', 'active'];

export default {
  name: 'ProgramModificationsDialog',

  components: {
    HrCheckbox,
    HrConfirmBtn,
    HrDismissBtn,
    HrFormDialog,
    HrFormSectionHeader,
    HrInfoBanner,
    HrListSelect,
    HrTextField,
    HrWarningBanner,
  },

  mixins: [
    Activities,
    ProgramsMixin,
    Toolbox,
  ],

  props: {
    incomingPrograms: {
      type: Array,
      required: true,
      default: () => [],
    },
    isNewProgram: {
      type: Boolean,
      default: false,
    },
    isManuallyOpened: {
      type: Boolean,
      default: false,
    },
    sessionId: {
      type: String,
      default: null,
    },
    isSessionAssignmentMode: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['hide', 'ok'],

  setup() {
    const { adjustDate } = useTime();
    return { adjustDate };
  },

  data() {
    return {
      isSaving: false,

      activityNotes: '',
      addPhaseLine: false,
      addPhaseLineLabel: '',
      addToSessions: [],
      newProgramStatus: '',
    };
  },

  computed: {
    ...mapState(sessions, ['scheduledSessions']),
    ...mapState(auth, ['userDetails', 'currentClient', 'hasTherapistCredentials']),

    canSave() {
      return size(this.addToSessions) > 0
        || (this.hasPhaseLine && (size(this.addPhaseLineLabel.trim()) > 0))
        || size(this.activityNotes.trim()) > 0;
    }, // canSave()

    canAddToSession() {
      return (this.isNewProgram || this.isSessionAssignmentMode) && size(this.visibleScheduledSessions) > 0;
    }, // canAddToSession()

    hasPhaseLine() {
      return (this.addPhaseLine || (!!this.addPhaseLineLabel.trim()));
    }, // hasPhaseLine()

    visibleScheduledSessions() {
      return this.naturalSort(
        filter(this.scheduledSessions, (session) => session.status === 'scheduled' && !session.isDeleted),
        'sessionName',
      );
    }, // visibleScheduledSessions()

    singleProgram() {
      return this.incomingPrograms[0] || {};
    }, // singleProgram()

    isBulkMode() {
      return this.incomingPrograms.length > 1;
    }, // isBulkMode()

    showProgramModificationFields() {
      return !this.isNewProgram && !this.isSessionAssignmentMode && !this.isBulkMode;
    }, // showProgramModificationFields()

    inactivePrograms() {
      return this.incomingPrograms.filter((p) => p.type !== 'behaviour'
        && INACTIVE_STATUSES.includes(p?.status));
    }, // inactivePrograms()

    showWarningBlock() {
      return this.inactivePrograms.length > 0 && this.addToSessions.length > 0;
    }, // showWarningBlock()

    statusOptions() {
      let keepExistingLabel = this.$t('editProgram.programModificationsDialog.keepExistingStatus');

      if (!this.isBulkMode && this.singleProgram?.status) {
        const statusConfig = programStatuses[this.singleProgram.status];

        if (statusConfig) {
          const currentStatusText = this.$t(statusConfig.label);
          keepExistingLabel = `${keepExistingLabel} (${currentStatusText})`;
        }
      }

      return [
        {
          label: keepExistingLabel,
          value: '',
        },
        ...AVAILABLE_STATUS_OPTIONS.map((status) => ({
          label: this.$t(programStatuses[status].label),
          value: status,
        })),
      ];
    }, // statusOptions()

  },

  methods: {
    // following method is REQUIRED
    // (don't change its name --> "show")
    async show() {
      this.$refs.dialog.show();
    },

    // following method is REQUIRED
    // (don't change its name --> "hide")
    hide() {
      if (this.$refs.dialog) {
        this.$refs.dialog.hide();
      }
    },

    onDialogHide() {
      // required to be emitted
      // when QDialog emits "hide" event
      this.$emit('hide');
    },

    async onSubmit() {
      this.isSaving = true;
      try {
        await this.save();
        this.$emit('ok', this.activityNotes.trim());
        this.hide();
      } catch (err) {
        this.notifyNegative(err.message);
      } finally {
        this.isSaving = false;
      }
    },

    onSetAddToSession(sessionId, value) {
      if (value) {
        this.addToSessions = [...this.addToSessions, sessionId];
      } else {
        this.addToSessions = without(this.addToSessions, sessionId);
      }
    }, // onSetAddToSession()

    addToSession(sessionId) {
      return this.addToSessions.includes(sessionId);
    }, // addToSession()

    async save() {
      if (this.showProgramModificationFields) {
        await Promise.all([
          this.saveActivity(),
          this.savePhaseLine(),
        ]);
      }
      if (this.canAddToSession && size(this.addToSessions) > 0) {
        await this.saveProgramStatus();
        await this.saveSessionPrograms();
      }
    }, // save()

    saveActivity() {
      if (size(this.activityNotes.trim()) === 0) {
        return;
      }
      const activityInfo = {
        originalSession: null,
        session: this.sessionId,
      };
      const eventInfo = {
        programId: this.singleProgram.id,
        programName: this.singleProgram.programName,
        notes: this.activityNotes,
        userName: this.userDetails.name,
      };
      return this.addActivity('programModification', false, eventInfo, activityInfo, true, 'program');
    }, // saveActivity()

    async savePhaseLine() {
      if (!this.hasPhaseLine || (size(this.addPhaseLineLabel.trim()) === 0)) {
        return;
      }
      const fromDatePreset = this.adjustDate(new Date(), { hour: 0, minute: 0, second: 0 });
      const startTimestamp = Timestamp.fromDate(fromDatePreset);

      const phaseLine = {
        clinic: this.userDetails.clinic,
        client: this.singleProgram.client,
        addedBy: this.userDetails.userId,

        startTimestamp,
        label: this.addPhaseLineLabel,
        scope: 'program',
        program: this.singleProgram.id,
        target: null,
      };

      const chartPhaseLinesDb = new ChartPhaseLinesDB();
      return chartPhaseLinesDb.create(phaseLine);
    }, // savePhaseLine()

    async saveSessionPrograms() {
      const targetPrograms = this.incomingPrograms.filter((p) => p.type !== 'behaviour');

      if (isEmpty(targetPrograms)) {
        return;
      }

      const saveActions = map(this.addToSessions, async (sessionId) => {
        const sessionProgramsDb = new SessionProgramsDB(sessionId);
        const lastSessionPrograms = await sessionProgramsDb.readAll(null, 'order', 'desc', 1);
        let currentMaxOrder = chain(lastSessionPrograms)
          .head()
          .get('order', 0)
          .value();

        let existingIds = [];
        if (!this.isNewProgram) {
          const existing = await sessionProgramsDb.readAll();
          existingIds = map(existing, (e) => (typeof e.program === 'string' ? e.program : e.program?.id));
        }

        const programActions = map(targetPrograms, (prog) => {
          // Prevent adding the same program multiple times to the same session
          if (!this.isNewProgram && existingIds.includes(prog.id)) {
            return null;
          }

          currentMaxOrder += 1;

          const sessionProgram = {
            session: sessionId,
            programName: prog.programName,
            status: 'created',
            program: prog.id,
            selectedTargets: this.getDefaultTargets(prog),
            pinned: false,
            hidden: false,
            order: currentMaxOrder,
          };

          if (!sessionProgram.program) {
            return null;
          }

          return sessionProgramsDb.create(sessionProgram);
        });
        return Promise.all(programActions.filter(Boolean));
      });
      await Promise.all(saveActions);
    }, // saveSessionPrograms()

    /**
     * Updates the status of all inactive programs selected in the dialog.
     * * Logic:
     * - Skips programs if their current status already matches the new selection.
     * - Updates status and 'statusChanges' map in Firestore.
     * - Records a 'programStatusModification' activity for the timeline.
     * * @returns {Promise<Array|void>} A promise that resolves when all updates and activities are saved.
     */
    async saveProgramStatus() {
      if (this.showWarningBlock && this.newProgramStatus) {
        const programsDb = new ProgramsDB();

        const updateActions = this.inactivePrograms.map(async (prog) => {
          if (prog.status === this.newProgramStatus) return null;

          const previousStatus = prog.status;
          const updatedStatusChanges = {
            ...(prog.statusChanges || {}),
            [this.newProgramStatus]: Timestamp.now(),
          };

          await programsDb.update({
            id: prog.id,
            status: this.newProgramStatus,
            statusChanges: updatedStatusChanges,
          });

          return this.addProgramStatusModificationActivity(
            prog,
            previousStatus,
            this.newProgramStatus,
          );
        });

        return Promise.all(updateActions.filter(Boolean));
      }
    }, // saveProgramStatus()

  },
};
</script>
